import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TileLayer } from 'react-leaflet';
import type L from 'leaflet';
import { SupplyShedClipPane } from './SupplyShedClipPane';
import type { GeoJSON as GeoJSONType } from '../../lib/types';

interface AgbCrossfadeOverlayProps {
  /**
   * URL for the year the user is currently asking for — comes from
   * `useAgbOverlay(agbYear)` and changes whenever the slider commits a
   * new year. Null when the overlay should not render at all.
   */
  pendingUrl: string | null;
  boundary: GeoJSONType.Polygon | null;
  /**
   * Lat/lng bbox of the supply shed — passed straight through to the
   * underlying TileLayer so Leaflet only fetches tile coords that
   * intersect the shed (the rest are masked by the pane's clip-path
   * anyway).
   */
  bounds?: L.LatLngBounds;
  /** Flips off any time AGB isn't the active data layer. */
  enabled: boolean;
  /**
   * Reports whether the pending layer is still streaming tiles. Wired from
   * Leaflet's stock `loading`/`load` events so the slider can surface a
   * loading indicator.
   */
  onLoadingChange?: (loading: boolean) => void;
  /**
   * Top of the source tile pyramid. Forwarded to both TileLayers so
   * Leaflet upscales these tiles past `maxNativeZoom` instead of fetching
   * coords that don't exist in the pyramid (which would 404). Without
   * this, dropping the basemap's hard zoom cap exposes the gap.
   */
  maxNativeZoom?: number;
}

/**
 * Two-layer renderer that crossfades between years instead of going blank
 * while new tiles paint:
 *
 *   backdrop (z=298) — last fully-loaded year; stays mounted as a stable
 *                       underlay during a year swap. CSS in globals.css
 *                       skips Leaflet's per-tile fade-in for this pane so
 *                       it snaps in at full opacity (true crossfade).
 *   pending  (z=300) — the year the user just scrubbed to; tiles fade in
 *                       on top as new tiles return.
 *
 * When `pending` fires its Leaflet `load` event — i.e. every visible tile
 * for the new year has rendered — we promote it to `backdrop` and stop
 * rendering the old layer. In steady state (pending === backdrop), only
 * one layer is on the map.
 *
 * Tiles come from a precomputed S3 pyramid (no auth, browser-cached for
 * 30 days), so each year scrub is one CDN GET per tile cold and a
 * memory-cache hit warm.
 */
export const AgbCrossfadeOverlay: React.FC<AgbCrossfadeOverlayProps> = ({
  pendingUrl,
  boundary,
  bounds,
  enabled,
  onLoadingChange,
  maxNativeZoom,
}) => {
  // `backdropUrl` holds the URL of the last year whose tiles fully painted.
  // Starts null so the very-first-load paints against the plain basemap
  // rather than a made-up backdrop of the same year (which would force a
  // redundant tile fetch and cause visible tile-paint stutter).
  const [backdropUrl, setBackdropUrl] = useState<string | null>(null);

  // Reset backdrop when the overlay is disabled so turning AGB back on
  // later doesn't leak a stale year.
  useEffect(() => {
    if (!enabled) setBackdropUrl(null);
  }, [enabled]);

  // Stable ref bag for the TileLayer event handlers. We CANNOT pass an
  // inline `eventHandlers={{ ... }}` to <TileLayer> here: react-leaflet's
  // useEventHandlers cleanup detaches old handlers before the URL-update
  // effect runs in the same commit, and Leaflet fires its `loading` event
  // synchronously during setUrl(). With inline handlers, every URL change
  // detaches the old handler, then setUrl fires `loading` with no listener,
  // and the slider's `isLoading` flag never flips true → never flips back
  // false → the slider's autoplay effect (which re-runs on isLoading
  // transitions) never schedules its next advance. By memoizing the
  // handler object once and reading current state through a ref, the
  // handlers stay attached for the layer's lifetime.
  //
  // `mounted` guards setBackdropUrl in the load handler — a tile fetch
  // that completes after the user toggles AGB off (or navigates away)
  // would otherwise trigger a setState on an unmounted component.
  const handlerStateRef = useRef({ pendingUrl, backdropUrl, onLoadingChange });
  handlerStateRef.current = { pendingUrl, backdropUrl, onLoadingChange };
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Tracks the URL we last saw a `loading` event for. Leaflet's `load`
  // event has no URL payload — without this, a load fired during a rapid
  // scrub (year A's tiles cached → load fires synchronously while the user
  // has already moved on to year B) would promote the *current*
  // pendingUrl as backdrop even though year B is still streaming, and
  // flip the loading indicator off prematurely. By comparing the URL the
  // most recent `loading` was tagged with to the current pendingUrl, we
  // ignore stale loads and wait for the next loading→load cycle to
  // resolve the user's actual position.
  const lastLoadingUrlRef = useRef<string | null>(null);
  const pendingHandlers = useMemo(
    () => ({
      loading: () => {
        lastLoadingUrlRef.current = handlerStateRef.current.pendingUrl;
        handlerStateRef.current.onLoadingChange?.(true);
      },
      load: () => {
        const { pendingUrl: pu, backdropUrl: bu, onLoadingChange: olc } =
          handlerStateRef.current;
        // Stale-load guard. When loadingRef diverges from the current
        // pendingUrl, the user scrubbed past mid-load. The next loading
        // fired by setUrl for the new URL will reset loadingRef and the
        // matching load will resolve cleanly — drop this one.
        if (
          lastLoadingUrlRef.current !== null &&
          lastLoadingUrlRef.current !== pu
        ) {
          return;
        }
        olc?.(false);
        if (mountedRef.current && pu !== null && pu !== bu) setBackdropUrl(pu);
      },
    }),
    [],
  );

  if (!enabled || !boundary || pendingUrl === null) {
    return null;
  }

  // Backdrop pane stays mounted whenever any year has fully loaded, but
  // visibility toggles via opacity rather than mount/unmount. In steady
  // state (backdropUrl === pendingUrl) it sits at 0; during a swap it
  // ramps to 0.9 so the visible composite alpha stays pinned at 0.9 across
  // the steady→swap boundary.
  const showBackdropPane = backdropUrl !== null;
  const backdropOpacity = backdropUrl === pendingUrl ? 0 : 0.9;

  return (
    <>
      {showBackdropPane && (
        <SupplyShedClipPane
          boundary={boundary}
          paneName="agb-backdrop-pane"
          zIndex={298}
        >
          <TileLayer
            url={backdropUrl!}
            opacity={backdropOpacity}
            bounds={bounds}
            maxNativeZoom={maxNativeZoom}
          />
        </SupplyShedClipPane>
      )}
      <SupplyShedClipPane
        boundary={boundary}
        paneName="agb-pending-pane"
        zIndex={300}
      >
        <TileLayer
          url={pendingUrl}
          opacity={0.9}
          bounds={bounds}
          maxNativeZoom={maxNativeZoom}
          eventHandlers={pendingHandlers}
        />
      </SupplyShedClipPane>
    </>
  );
};
